import React, { useState } from 'react';
import { useMount } from 'react-use';
import Modal from 'shared/Modal';
import styled from 'styled-components';
import {
  Button,
  Collapse,
  TableHead,
  TableCell,
  Table,
  TableRow,
  TableBody,
  Tooltip,
  Box,
} from '@mui/material';
import CollapseCell from 'components/CollapseCell';
import RefreshIcon from '@mui/icons-material/Refresh';
import JsonViewer from 'shared/JsonViewer';
import { format, parseISO } from 'date-fns';
import useLocalDispatch from 'hooks/useLocalDispatch';
import { fetchProvisionErrors as _fetchProvisionErrors } from 'thunk';
import { LinearBuffer } from 'components/Progress';
import { ProvisionError } from 'IntegrationModule/api/provisionIntegrationApi';
import { useNotification } from 'hooks/useNotification';

const ModalHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalHeader = styled.h3``;

const provisionErrorTableHeads = [
  { label: '', id: 'collapse' },
  {
    label: 'Time',
    id: 'createdAt',
  },
  {
    label: 'Session ID',
    id: 'sessionId',
  },
  {
    label: 'Payload',
    id: 'payload',
  },
  {
    label: 'Response',
    id: 'response',
  },
];

const stateId = 'fetch-provision-errors';

type ProvisionErrorTableModalProps = {
  teamId: number;
  isModalOpen: boolean;
  toggleProvisionErrorTableModal: (nextValue?: any) => void;
};

const ProvisionErrorTableModal = ({
  teamId,
  toggleProvisionErrorTableModal,
}: ProvisionErrorTableModalProps) => {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const { dispatchLocal, selectLocalData } = useLocalDispatch();
  const { showError } = useNotification();
  const {
    data: provisionErrorData,
    error,
    isRequesting,
  } = selectLocalData<{
    data: ProvisionError[];
  }>({
    stateId,
  });

  const fetchProvisionErrors = () => {
    const params = { teamId };
    dispatchLocal(_fetchProvisionErrors, {
      params,
      stateId,
      formatSuccessResponse: ({ response }) => {
        return {
          data: response.data,
        };
      },
      onSuccessCallback: ({ response }) => {
        const error = response.data?.errors?.[0];
        if (error) {
          // ✅ FIX: Replace alert() with toast notification
          showError(`Failed fetch provision errors. ${error}`);
        }
      },
    });
  };

  useMount(() => {
    fetchProvisionErrors();
  });

  const onCollapse = (id: string) => () => {
    setExpandedRows({
      ...expandedRows,
      [id]: !expandedRows[id],
    });
  };

  const renderTableHead = () => (
    <TableHead>
      {provisionErrorTableHeads.map((heads) => (
        <TableCell key={heads.id}>{heads.label}</TableCell>
      ))}
    </TableHead>
  );

  const renderHeadRow = (headCellId: string, data: ProvisionError) => {
    switch (headCellId) {
      case 'collapse':
        return (
          <CollapseCell
            isExpanded={expandedRows[data.id.toString()]}
            onCollapse={onCollapse(data.id.toString())}
          />
        );
      case 'createdAt':
        return format(parseISO(data.createdAt), 'yyyy-MM-dd HH:mm');
      case 'sessionId':
        return data.sessionId;
      case 'response':
        return data?.response?.error ? (
          <JsonViewer src={JSON.parse(data.response.error)} />
        ) : data?.response ? (
          'See expanded Row'
        ) : (
          'Not found'
        );
      default:
        return 'See expanded Row';
    }
  };

  const renderCellData = {
    payload: (data: any) => (
      <Box>
        <JsonViewer src={data.payload} />
      </Box>
    ),
    response: (data: any) => (
      <Box
        sx={{
          width: '400px',
        }}
      >
        <JsonViewer src={JSON.parse(data.response)} />
      </Box>
    ),
  };

  const renderCollapsedRow = (headCellId: string, data: ProvisionError) => {
    const cellRenderer = renderCellData[headCellId as keyof typeof renderCellData];

    if (!data || !cellRenderer) return null;

    return (
      <div style={{ padding: '16px', flex: 1 }}>
        {headCellId}: {cellRenderer(data)}
      </div>
    );
  };

  const renderRows = () => {
    return provisionErrorData?.data.map((data: ProvisionError) => {
      return (
        <TableBody key={data.id}>
          <TableRow>
            {provisionErrorTableHeads.map((head) => (
              <TableCell key={head.id}>{renderHeadRow(head.id, data)}</TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
              <Collapse in={expandedRows[data.id]} timeout="auto" unmountOnExit>
                <div style={{ display: 'flex' }}>
                  {provisionErrorTableHeads.map((head) => renderCollapsedRow(head.id, data))}
                </div>
              </Collapse>
            </TableCell>
          </TableRow>
        </TableBody>
      );
    });
  };

  return (
    <Modal modalWidth={800} open onClose={toggleProvisionErrorTableModal} modalMaxHeight={800}>
      <div>
        <ModalHeaderContainer>
          <Tooltip
            title="This view includes the only most recent 2 hours of provision calls for this team"
            placement="bottom-start"
          >
            <ModalHeader>Provision Errors</ModalHeader>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            onClick={fetchProvisionErrors}
            startIcon={<RefreshIcon />}
          >
            Refresh
          </Button>
        </ModalHeaderContainer>
        <Box>
          {isRequesting ? (
            <LinearBuffer />
          ) : (
            <Table>
              {renderTableHead()}
              {renderRows()}
            </Table>
          )}
        </Box>
      </div>
    </Modal>
  );
};

export default ProvisionErrorTableModal;
